import React, { useCallback, useMemo, useState } from "react";
import {
  StyleSheet,
  Text,
  View as RNView,
  Pressable,
  ScrollView,
  Image,
  Linking,
  Platform,
  Alert,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import * as Sharing from "expo-sharing";
// SDK 54+: import from "expo-file-system/legacy" instead
import * as FileSystem from "expo-file-system";
import { useDispatch, useSelector } from "react-redux";
import { useAppTheme } from "../../../../hooks/useAppTheme";
import { ThemeColors } from "../../../../utils/constants/colors";
import { View } from "../../../../components/shared/Themed";
import statusColorCodes from "../../../../utils/constants/statusColorCodes";
import {
  getOrganizationOrders,
  updateSalesOrder,
} from "../../../../redux/dispatcher/portalDispatcher";
// Adjust these two paths to where the modal and the shared loaders live
import CreateSalesOrderModal, {
  EditableOrder,
  SalesOrderPayload,
} from "../../../Customer/admin/CreateSalesOder";
import { useSalesOrderLoaders } from "../utils/SalesOrderLoader";

// ─── Design Tokens (same as the organizations / orders screens) ──────────────
const getC = (colors: ThemeColors, isDark: boolean) => ({
  isDark,
  navy: isDark ? colors.surface : "#0F1B2D",
  accent: colors.primary,
  success: colors.success,
  successBg: isDark ? "rgba(14, 159, 110, 0.2)" : "#EDFAF4",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  warning: colors.warning,
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F5F7FB",
  border: colors.borderSubtle,
  borderLight: colors.borderSubtle,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
});

type Tokens = ReturnType<typeof getC>;

const useTokens = () => {
  const { colors, isDark } = useAppTheme();
  return useMemo(() => getC(colors, isDark), [colors, isDark]);
};

const WIDE_BREAKPOINT = 900;
const CONTENT_MAX_WIDTH = 1280;

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderItem {
  _id: string;
  uniqueKey?: string;
  itemCodes?: string[];
  pictureName?: string | null;
  appointments?: Appointment[];
  itemCode?: string;
  itemName?: string;
  requiredDate?: string;
  unitPrice?: number | null;
  quantity?: number;
  uom?: "Pallets" | "Boxes" | "Pieces" | string;
  perPallet?: number;
  perCtns?: number;
  totalPrice?: number | null;
  deliveryTo?: string;
  pickUpFrom?: string;
  documentNumber?: string | null;
  imageUrl?: string | null;
  warehouse?: { _id: string; name: string } | null;
}

interface Appointment {
  _id: string;
  startDateTime?: string;
  endDateTime?: string;
  summary?: string;
}

interface Order {
  _id: string;
  orderNo?: string;
  orderStatus?: string;
  requestType?: string;
  poNumber?: string;
  remarks?: string;
  createdAt?: string;
  updatedAt?: string;
  createdByEmail?: string | null;
  createdBy?: { auth?: { email?: string | null } } | null;
  approvedBy?: string;
  rejectedBy?: string;
  completedBy?: string;
  organization?: { _id: string; name: string; cardCode?: string | string[] };
  subOrganization?: { _id: string; name: string } | null;
  details?: OrderItem[];
  appointments?: Appointment[];
  subtotal?: number | null;
  taxAmount?: number | null;
  taxRate?: number | null;
  grandTotal?: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (v?: string) =>
  v
    ? new Date(v).toLocaleString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const formatTimeRange = (a: Appointment) => {
  if (!a.startDateTime) return "—";
  const start = new Date(a.startDateTime);
  const day = start.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const t = (d: Date) =>
    d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  return a.endDateTime
    ? `${day}, ${t(start)} – ${t(new Date(a.endDateTime))}`
    : `${day}, ${t(start)}`;
};

const money = (n?: number | null) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const num = (n: number) => n.toLocaleString();

/** Pieces for one line, same rules as the web summary. */
const itemPieces = (i: OrderItem) => {
  const q = i.quantity ?? 0;
  if (i.uom === "Pallets" && i.perPallet && i.perCtns)
    return q * i.perPallet * i.perCtns;
  if (i.uom === "Boxes" && i.perCtns) return q * i.perCtns;
  if (i.uom === "Pieces") return q;
  return 0;
};

const totals = (items: OrderItem[]) =>
  items.reduce(
    (acc, i) => {
      const q = i.quantity ?? 0;
      if (i.uom === "Pallets") acc.pallets += q;
      if (i.uom === "Pallets" && i.perPallet) acc.boxes += q * i.perPallet;
      if (i.uom === "Boxes") acc.boxes += q;
      acc.pieces += itemPieces(i);
      return acc;
    },
    { pallets: 0, boxes: 0, pieces: 0 },
  );

/** Display-only names; the backend value is unchanged */
const STATUS_LABELS: Record<string, string> = {
  "Pending For Paperwork": "Progressing",
};
const statusLabel = (status?: string | null) =>
  (status && STATUS_LABELS[status]) || status || "";

/** Sales reps can edit an order until it's been approved */
const EDITABLE_STATUSES = ["Pending For Approval", "Pending For Paperwork"];

const statusColor = (status: string | undefined, C: Tokens) =>
  (status && (statusColorCodes as Record<string, string>)[status]) ||
  C.textMuted;

// Progress steps for the tracker
const STEPS = [
  { key: "placed", label: "Placed", icon: "file-text" },
  { key: "approved", label: "Approved", icon: "check-circle" },
  { key: "ready", label: "Ready", icon: "package" },
  { key: "shipped", label: "Shipped", icon: "truck" },
] as const;

const stepIndex = (status?: string) => {
  switch (status) {
    case "Approved":
      return 1;
    case "Ready":
      return 2;
    case "Shipped":
      return 3;
    default:
      return 0; // Pending For Approval / Paperwork, Back-Order, Cancelled
  }
};

const openUrl = (url?: string | null) => {
  if (url) Linking.openURL(url).catch(() => {});
};

// ─── Export (CSV / JSON) ──────────────────────────────────────────────────────

const SUPPLIER = "Multisteps";

const round = (n: number, dp = 2) =>
  Number.isFinite(n) ? Math.round(n * 10 ** dp) / 10 ** dp : null;

const isoDate = (v?: string) => (v ? new Date(v).toISOString() : null);
const auDate = (v?: string) =>
  v ? new Date(v).toLocaleDateString("en-AU") : null;

/** perCtns = units per box, perPallet = boxes per pallet (as on the web summary). */
const itemQuantities = (i: OrderItem) => {
  const q = i.quantity ?? 0;
  const perBox = i.perCtns || 0;
  const perPallet = i.perPallet || 0;
  const unitsPerPallet = perBox * perPallet;
  const units = itemPieces(i);
  const boxes =
    i.uom === "Boxes"
      ? q
      : i.uom === "Pallets"
        ? perPallet
          ? q * perPallet
          : null
        : perBox
          ? q / perBox
          : null;
  const pallets =
    i.uom === "Pallets"
      ? q
      : i.uom === "Boxes"
        ? perPallet
          ? q / perPallet
          : null
        : unitsPerPallet
          ? q / unitsPerPallet
          : null;
  return {
    unitsPerBox: perBox || null,
    boxesPerPallet: perPallet || null,
    unitsPerPallet: unitsPerPallet || null,
    pallets: pallets == null ? null : round(pallets),
    boxes: boxes == null ? null : round(boxes),
    units,
  };
};

const unitPrice = (i: OrderItem) => {
  if (i.unitPrice != null) return i.unitPrice;
  const units = itemPieces(i);
  return i.totalPrice != null && units ? round(i.totalPrice / units, 4) : null;
};

/** Everything on the summary, as one structured object. */
const buildExport = (order: Order) => {
  const items = order.details ?? [];
  const sums = totals(items);
  return {
    order: {
      order_number: order.orderNo ?? null,
      status: order.orderStatus ?? null,
      request_type: order.requestType ?? null,
      po_number: order.poNumber ?? null,
      organization: order.organization?.name ?? null,
      card_code: ([] as string[])
        .concat(order.organization?.cardCode ?? [])
        .join("; "),
      grower: order.subOrganization?.name ?? null,
      placed_by: order.createdByEmail ?? order.createdBy?.auth?.email ?? null,
      created_at: isoDate(order.createdAt),
      updated_at: isoDate(order.updatedAt),
      approved_by: order.approvedBy ?? null,
      rejected_by: order.rejectedBy ?? null,
      completed_by: order.completedBy ?? null,
      remarks: order.remarks ?? null,
    },
    totals: {
      items: items.length,
      pallets: sums.pallets,
      boxes: sums.boxes,
      units: sums.pieces,
      subtotal_excl_tax: order.subtotal ?? null,
      tax_rate: order.taxRate ?? null,
      tax_amount: order.taxAmount ?? null,
      grand_total_incl_tax: order.grandTotal ?? null,
    },
    appointments: (order.appointments ?? []).map((a) => ({
      start: isoDate(a.startDateTime),
      end: isoDate(a.endDateTime),
      summary: a.summary ?? null,
    })),
    items: items.map((i) => {
      const q = itemQuantities(i);
      return {
        item_code: i.itemCode ?? null,
        item_name: i.itemName ?? null,
        supplier: SUPPLIER,
        warehouse: i.warehouse?.name ?? null,
        document_number: i.documentNumber ?? null,
        required_date: isoDate(i.requiredDate),
        uom: i.uom ?? null,
        quantity: i.quantity ?? 0,
        units_per_box: q.unitsPerBox,
        boxes_per_pallet: q.boxesPerPallet,
        units_per_pallet: q.unitsPerPallet,
        pallets_ordered: q.pallets,
        boxes_ordered: q.boxes,
        total_units: q.units,
        unit_price: unitPrice(i),
        line_total: i.totalPrice ?? null,
        deliver_to:
          order.requestType === "Pick up" ? null : (i.deliveryTo ?? null),
        pickup_from: i.pickUpFrom ?? null,
      };
    }),
  };
};

const csvCell = (v: unknown) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** One row per item; order details and totals repeat on every row. */
const buildCsv = (order: Order) => {
  const data = buildExport(order);
  const o = data.order;
  const t = data.totals;
  const orderCols: [string, unknown][] = [
    ["order_number", o.order_number],
    ["order_date", auDate(order.createdAt)],
    ["status", o.status],
    ["request_type", o.request_type],
    ["po_number", o.po_number],
    ["organization", o.organization],
    ["card_code", o.card_code],
    ["grower", o.grower],
    ["placed_by", o.placed_by],
  ];
  const totalCols: [string, unknown][] = [
    ["order_subtotal_excl_tax", t.subtotal_excl_tax],
    ["order_tax_rate", t.tax_rate],
    ["order_tax_amount", t.tax_amount],
    ["order_grand_total_incl_tax", t.grand_total_incl_tax],
    ["approved_by", o.approved_by],
    ["completed_by", o.completed_by],
    ["remarks", o.remarks],
  ];
  const itemKeys = data.items[0] ? Object.keys(data.items[0]) : [];

  const header = [
    ...orderCols.map(([k]) => k),
    ...itemKeys,
    ...totalCols.map(([k]) => k),
  ];
  const rows = data.items.map((it: Record<string, unknown>) => [
    ...orderCols.map(([, v]) => v),
    ...itemKeys.map((k) => it[k]),
    ...totalCols.map(([, v]) => v),
  ]);
  // BOM so Excel opens UTF-8 (e.g. "–", accented names) correctly
  return (
    "﻿" + [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n")
  );
};

const saveFile = async (filename: string, content: string, mime: string) => {
  if (Platform.OS === "web") {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }
  const uri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(uri, content, {
    encoding: FileSystem.EncodingType.UTF8,
  });
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error("Sharing isn't available on this device.");
  }
  await Sharing.shareAsync(uri, { mimeType: mime, dialogTitle: filename });
};

const exportFileName = (order: Order, ext: string) =>
  `order_${(order.orderNo || order._id).replace(/[^\w-]+/g, "_")}.${ext}`;

// ─── Small pieces ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, C }: { status?: string; C: Tokens }) => {
  if (!status) return null;
  const color = statusColor(status, C);
  return (
    <RNView
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        alignSelf: "flex-start",
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 999,
        backgroundColor: color + "1A",
      }}
    >
      <RNView
        style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: color }}
      />
      <Text style={{ fontSize: 12, fontWeight: "800", color }}>
        {statusLabel(status)}
      </Text>
    </RNView>
  );
};

const Card = ({
  title,
  icon,
  right,
  children,
  style,
  S,
  C,
}: {
  title?: string;
  icon?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  style?: any;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => (
  <RNView style={[S.card, style]}>
    {!!title && (
      <RNView style={S.cardHead}>
        {!!icon && (
          <RNView style={S.cardIcon}>
            <FeatherIcon name={icon} size={15} color={C.accent} />
          </RNView>
        )}
        <Text style={S.cardTitle}>{title}</Text>
        <RNView style={{ flex: 1 }} />
        {right}
      </RNView>
    )}
    {children}
  </RNView>
);

const Field = ({
  label,
  value,
  icon,
  S,
  C,
  wide,
}: {
  label: string;
  value?: string | null;
  icon?: string;
  S: ReturnType<typeof createS>;
  C: Tokens;
  wide?: boolean;
}) => (
  <RNView style={[S.field, wide && S.fieldWide]}>
    <RNView style={S.fieldLabelRow}>
      {!!icon && <FeatherIcon name={icon} size={12} color={C.textMuted} />}
      <Text style={S.fieldLabel}>{label}</Text>
    </RNView>
    <Text style={S.fieldValue} selectable>
      {value || "—"}
    </Text>
  </RNView>
);

const ExportButton = ({
  label,
  icon,
  busy,
  disabled,
  onPress,
  S,
  C,
}: {
  label: string;
  icon: string;
  busy: boolean;
  disabled: boolean;
  onPress: () => void;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => (
  <Pressable
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={`Export ${label}`}
    style={(s: any) => [
      S.exportBtn,
      (s.hovered || s.pressed) && S.hover,
      disabled && !busy && { opacity: 0.5 },
    ]}
  >
    {busy ? (
      <ActivityIndicator size="small" color={C.accent} />
    ) : (
      <FeatherIcon name={icon} size={14} color={C.accent} />
    )}
    <Text style={S.exportText}>{label}</Text>
  </Pressable>
);

// ─── Status tracker ───────────────────────────────────────────────────────────

const Tracker = ({
  status,
  S,
  C,
}: {
  status?: string;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => {
  const current = stepIndex(status);
  const halted = status === "Cancelled" || status === "Back-Order";
  const haltedColor = statusColor(status, C);

  return (
    <RNView style={S.card}>
      <RNView style={S.tracker}>
        {STEPS.map((step, i) => {
          const done = i < current || (i === current && !halted && i > 0);
          const active = i === current;
          const color =
            active && halted
              ? haltedColor
              : done || active
                ? C.accent
                : C.border;
          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <RNView
                  style={[
                    S.trackLine,
                    { backgroundColor: i <= current ? C.accent : C.border },
                  ]}
                />
              )}
              <RNView style={S.trackStep}>
                <RNView
                  style={[
                    S.trackDot,
                    {
                      borderColor: color,
                      backgroundColor: done || active ? color : "transparent",
                    },
                  ]}
                >
                  <FeatherIcon
                    name={active && halted ? "alert-circle" : step.icon}
                    size={14}
                    color={done || active ? "#fff" : C.textMuted}
                  />
                </RNView>
                <Text
                  style={[
                    S.trackLabel,
                    (done || active) && { color: C.text, fontWeight: "800" },
                  ]}
                  numberOfLines={2}
                >
                  {active && i === 0 && status
                    ? statusLabel(status)
                    : step.label}
                </Text>
              </RNView>
            </React.Fragment>
          );
        })}
      </RNView>
      {halted && (
        <RNView style={[S.haltBanner, { backgroundColor: haltedColor + "14" }]}>
          <FeatherIcon name="alert-circle" size={14} color={haltedColor} />
          <Text style={[S.haltText, { color: haltedColor }]}>
            {status === "Cancelled"
              ? "This order was cancelled."
              : "Some items are on back-order."}
          </Text>
        </RNView>
      )}
    </RNView>
  );
};

// ─── Items ────────────────────────────────────────────────────────────────────

const ItemImage = ({
  url,
  size,
  S,
  C,
}: {
  url?: string | null;
  size: number;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => (
  <Pressable
    onPress={() => openUrl(url)}
    disabled={!url}
    accessibilityLabel={url ? "Open item image" : "No image"}
    style={[S.thumb, { width: size, height: size }]}
  >
    {url ? (
      <Image source={{ uri: url }} style={{ width: size, height: size }} />
    ) : (
      <FeatherIcon name="image" size={16} color={C.textMuted} />
    )}
  </Pressable>
);

const Location = ({
  icon,
  label,
  value,
  S,
  C,
}: {
  icon: string;
  label: string;
  value?: string | null;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) =>
  value ? (
    <RNView style={S.locRow}>
      <FeatherIcon name={icon} size={12} color={C.textMuted} />
      <Text style={S.locLabel}>{label}</Text>
      <Text style={S.locValue} numberOfLines={2}>
        {value}
      </Text>
    </RNView>
  ) : null;

const ItemsTable = ({
  items,
  isPickup,
  S,
  C,
}: {
  items: OrderItem[];
  isPickup: boolean;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => (
  <RNView style={S.table}>
    <RNView style={[S.tr, S.thead]}>
      <Text style={[S.th, { width: 52 }]} />
      <Text style={[S.th, S.cItem]}>Item</Text>
      <Text style={[S.th, S.cQty]}>Qty</Text>
      <Text style={[S.th, S.cPieces]}>Pieces</Text>
      <Text style={[S.th, S.cPrice]}>Total</Text>
      <Text style={[S.th, S.cLoc]}>{isPickup ? "Pickup from" : "Route"}</Text>
      <Text style={[S.th, S.cDoc]}>Document</Text>
    </RNView>
    {items.map((it, idx) => (
      <RNView
        key={it._id}
        style={[S.tr, idx === items.length - 1 && { borderBottomWidth: 0 }]}
      >
        <ItemImage url={it.imageUrl} size={40} S={S} C={C} />
        <RNView style={S.cItem}>
          <Text style={S.itemName} numberOfLines={2}>
            {it.itemName || "—"}
          </Text>
          {!!it.warehouse?.name && (
            <Text style={S.itemSub} numberOfLines={1}>
              {it.warehouse.name}
            </Text>
          )}
        </RNView>
        <RNView style={[S.cQty, S.qtyCell]}>
          <Text style={S.qty}>{num(it.quantity ?? 0)}</Text>
          {!!it.uom && <Text style={S.uom}>{it.uom}</Text>}
        </RNView>
        <Text style={[S.td, S.cPieces]}>{num(itemPieces(it))}</Text>
        <Text style={[S.td, S.cPrice, { fontWeight: "700", color: C.text }]}>
          {it.totalPrice != null ? money(it.totalPrice) : "—"}
        </Text>
        <RNView style={[S.cLoc, { gap: 4 }]}>
          {!isPickup && (
            <Location
              icon="map-pin"
              label="To"
              value={it.deliveryTo}
              S={S}
              C={C}
            />
          )}
          <Location
            icon={isPickup ? "home" : "navigation"}
            label={isPickup ? "" : "From"}
            value={it.pickUpFrom}
            S={S}
            C={C}
          />
        </RNView>
        <RNView style={S.cDoc}>
          {it.documentNumber ? (
            <RNView style={S.docChip}>
              <Text style={S.docText} selectable>
                {it.documentNumber}
              </Text>
            </RNView>
          ) : (
            <Text style={S.td}>—</Text>
          )}
        </RNView>
      </RNView>
    ))}
  </RNView>
);

const ItemCard = ({
  it,
  isPickup,
  S,
  C,
}: {
  it: OrderItem;
  isPickup: boolean;
  S: ReturnType<typeof createS>;
  C: Tokens;
}) => (
  <RNView style={S.itemCard}>
    <RNView style={S.itemTop}>
      <ItemImage url={it.imageUrl} size={48} S={S} C={C} />
      <RNView style={{ flex: 1, minWidth: 0 }}>
        <Text style={S.itemName} numberOfLines={2}>
          {it.itemName || "—"}
        </Text>
        {!!it.documentNumber && (
          <Text style={S.itemSub} selectable>
            Doc {it.documentNumber}
          </Text>
        )}
      </RNView>
    </RNView>
    <RNView style={S.itemStats}>
      {[
        ["Qty", `${num(it.quantity ?? 0)} ${it.uom ?? ""}`.trim()],
        ["Pieces", num(itemPieces(it))],
        ["Total", it.totalPrice != null ? money(it.totalPrice) : "—"],
      ].map(([k, v]) => (
        <RNView key={k} style={S.itemStat}>
          <Text style={S.itemStatLabel}>{k}</Text>
          <Text style={S.itemStatValue}>{v}</Text>
        </RNView>
      ))}
    </RNView>
    <RNView style={{ gap: 4 }}>
      {!isPickup && (
        <Location icon="map-pin" label="To" value={it.deliveryTo} S={S} C={C} />
      )}
      <Location
        icon={isPickup ? "home" : "navigation"}
        label={isPickup ? "Pickup" : "From"}
        value={it.pickUpFrom}
        S={S}
        C={C}
      />
    </RNView>
  </RNView>
);

// ─── Main Screen ──────────────────────────────────────────────────────────────

interface Props {
  navigation?: any;
  route?: {
    params?: { orderId?: string; orgId?: string; orgName?: string };
  };
}

const OrganizationOrderSummaryScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const C = useTokens();
  const S = useMemo(() => createS(C), [C]);
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;
  const isMedium = width >= 640;

  const orderId = route?.params?.orderId;
  const orgId = route?.params?.orgId;
  const dispatch = useDispatch<any>();
  const loaders = useSalesOrderLoaders();
  const [editOpen, setEditOpen] = useState(false);

  // The org orders list already loaded this order with its items
  const orders: Order[] = useSelector((state: any) => state.portal.orders);
  const order = useMemo(
    () => orders?.find((o) => String(o._id) === String(orderId)) ?? null,
    [orders, orderId],
  );

  const orgName =
    order?.organization?.name ?? route?.params?.orgName ?? "Organization";

  const goBack = () =>
    navigation?.navigate("OrganizationOrdersScreen", {
      orgId: orgId ?? order?.organization?._id,
      orgName,
    });

  const items = order?.details ?? [];
  const sums = useMemo(() => totals(items), [items]);
  const isPickup = order?.requestType === "Pick up";
  const placedBy = order?.createdByEmail ?? order?.createdBy?.auth?.email;

  // ── Edit (sales order update API) ───────────────────────────────────────
  const canEdit =
    !!order && EDITABLE_STATUSES.includes(order.orderStatus ?? "");

  const saveEdit = useCallback(
    async (payload: SalesOrderPayload) => {
      if (!order) return;
      // Throws on failure; the modal shows the server's message
      await dispatch(updateSalesOrder({ id: order._id, payload })).unwrap();
      setEditOpen(false);
      // This screen reads the order from the list, so reload it
      const org = orgId ?? order.organization?._id;
      if (org) dispatch(getOrganizationOrders(org));
    },
    [dispatch, order, orgId],
  );

  // ── Export ────────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState<"csv" | "json" | null>(null);

  const handleExport = async (kind: "csv" | "json") => {
    if (!order || exporting) return;
    setExporting(kind);
    try {
      if (kind === "csv") {
        await saveFile(
          exportFileName(order, "csv"),
          buildCsv(order),
          "text/csv;charset=utf-8",
        );
      } else {
        await saveFile(
          exportFileName(order, "json"),
          JSON.stringify(buildExport(order), null, 2),
          "application/json",
        );
      }
    } catch (e: any) {
      const msg = e?.message ?? "Couldn't export this order.";
      if (Platform.OS === "web") window.alert(msg);
      else Alert.alert("Export failed", msg);
    } finally {
      setExporting(null);
    }
  };

  // ── Not loaded (e.g. web refresh on this URL) ───────────────────────────
  if (!order) {
    return (
      <View style={[S.root, S.center]}>
        <RNView style={S.emptyIcon}>
          <FeatherIcon name="file-text" size={26} color={C.accent} />
        </RNView>
        <Text style={S.emptyTitle}>Order not available</Text>
        <Text style={S.emptySub}>
          Open it again from the organization's orders list.
        </Text>
        <Pressable onPress={goBack} style={S.emptyBtn}>
          <Text style={S.emptyBtnText}>Back to orders</Text>
        </Pressable>
      </View>
    );
  }

  const stats: [string, string][] = [
    ["Items", num(items.length)],
    ["Pallets", num(sums.pallets)],
    ["Boxes", num(sums.boxes)],
    ["Pieces", num(sums.pieces)],
  ];
  const money_: [string, string][] = [];
  if (order.subtotal != null)
    money_.push(["Subtotal (excl. tax)", money(order.subtotal)]);
  if (order.taxAmount != null)
    money_.push([
      order.taxRate != null
        ? `GST (${(order.taxRate * 100).toFixed(0)}%)`
        : "Tax",
      money(order.taxAmount),
    ]);

  return (
    <View style={S.root}>
      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <RNView style={S.header}>
        <RNView style={[S.headerInner, S.titleRow]}>
          <Pressable
            onPress={goBack}
            accessibilityLabel="Back to orders"
            hitSlop={8}
            style={(s: any) => [S.backBtn, (s.hovered || s.pressed) && S.hover]}
          >
            <FeatherIcon name="arrow-left" size={18} color={C.text} />
          </Pressable>
          <RNView style={{ flex: 1, minWidth: 0 }}>
            <Text style={S.eyebrow} numberOfLines={1}>
              {orgName} · Order summary
            </Text>
            <RNView style={S.titleLine}>
              <Text style={S.title} selectable numberOfLines={1}>
                {order.orderNo || "Order"}
              </Text>
              <StatusBadge status={order.orderStatus} C={C} />
            </RNView>
          </RNView>
          {canEdit && (
            <Pressable
              onPress={() => setEditOpen(true)}
              accessibilityRole="button"
              accessibilityLabel="Edit order"
              style={(s: any) => [
                S.editBtn,
                (s.hovered || s.pressed) && { opacity: 0.9 },
              ]}
            >
              <FeatherIcon name="edit-2" size={14} color="#fff" />
              {isMedium && <Text style={S.editText}>Edit order</Text>}
            </Pressable>
          )}
          {items.length > 0 && (
            <RNView style={S.exportRow}>
              <ExportButton
                label="CSV"
                icon="download"
                busy={exporting === "csv"}
                disabled={!!exporting}
                onPress={() => handleExport("csv")}
                S={S}
                C={C}
              />
              <ExportButton
                label="JSON"
                icon="code"
                busy={exporting === "json"}
                disabled={!!exporting}
                onPress={() => handleExport("json")}
                S={S}
                C={C}
              />
            </RNView>
          )}
        </RNView>
      </RNView>

      <ScrollView contentContainerStyle={S.content}>
        <RNView style={S.inner}>
          {/* ── Progress ── */}
          <Tracker status={order.orderStatus} S={S} C={C} />

          {/* ── Details ── */}
          <Card title="Order details" icon="info" S={S} C={C}>
            <RNView style={S.fieldGrid}>
              <Field
                label="Order number"
                icon="hash"
                value={order.orderNo}
                S={S}
                C={C}
                wide={isMedium}
              />
              <Field
                label="Request type"
                icon="truck"
                value={order.requestType}
                S={S}
                C={C}
                wide={isMedium}
              />
              <Field
                label="Created"
                icon="calendar"
                value={formatDateTime(order.createdAt)}
                S={S}
                C={C}
                wide={isMedium}
              />
              <Field
                label="Placed by"
                icon="mail"
                value={placedBy}
                S={S}
                C={C}
                wide={isMedium}
              />
              {!!order.poNumber && (
                <Field
                  label="PO / customer reference"
                  icon="tag"
                  value={order.poNumber}
                  S={S}
                  C={C}
                  wide={isMedium}
                />
              )}
            </RNView>
          </Card>

          {/* ── Organization + additional info ── */}
          <RNView style={[S.split, isWide && S.splitWide]}>
            <Card
              title="Organization"
              icon="briefcase"
              S={S}
              C={C}
              style={isWide && S.half}
            >
              <RNView style={{ gap: 14 }}>
                <Field
                  label="Main organization"
                  value={order.organization?.name}
                  S={S}
                  C={C}
                />
                <Field
                  label="Grower / sub-organization"
                  value={order.subOrganization?.name}
                  S={S}
                  C={C}
                />
              </RNView>
            </Card>
            <Card
              title="Additional information"
              icon="clock"
              S={S}
              C={C}
              style={isWide && S.half}
            >
              <RNView style={{ gap: 14 }}>
                <Field
                  label="Last updated"
                  value={formatDateTime(order.updatedAt)}
                  S={S}
                  C={C}
                />
                {!!order.remarks && (
                  <Field label="Remarks" value={order.remarks} S={S} C={C} />
                )}
                {!!order.approvedBy && (
                  <Field
                    label="Approved by"
                    value={order.approvedBy}
                    S={S}
                    C={C}
                  />
                )}
                {!!order.rejectedBy && (
                  <Field
                    label="Rejected by"
                    value={order.rejectedBy}
                    S={S}
                    C={C}
                  />
                )}
                {!!order.completedBy && (
                  <Field
                    label="Completed by"
                    value={order.completedBy}
                    S={S}
                    C={C}
                  />
                )}
              </RNView>
            </Card>
          </RNView>

          {/* ── Pickup appointments ── */}
          {isPickup && (order.appointments?.length ?? 0) > 0 && (
            <Card title="Pickup appointments" icon="calendar" S={S} C={C}>
              <RNView style={{ gap: 8 }}>
                {order.appointments!.map((a) => (
                  <RNView key={a._id} style={S.apptRow}>
                    <FeatherIcon name="clock" size={14} color={C.accent} />
                    <Text style={S.apptTime}>{formatTimeRange(a)}</Text>
                    {!!a.summary && (
                      <Text style={S.apptSummary} numberOfLines={1}>
                        {a.summary}
                      </Text>
                    )}
                  </RNView>
                ))}
              </RNView>
            </Card>
          )}

          {/* ── Totals band ── */}
          <RNView style={S.band}>
            <RNView style={[S.bandRow, !isMedium && S.bandRowWrap]}>
              {stats.map(([label, value]) => (
                <RNView
                  key={label}
                  style={[S.bandStat, !isMedium && S.bandStatHalf]}
                >
                  <Text style={S.bandValue}>{value}</Text>
                  <Text style={S.bandLabel}>{label}</Text>
                </RNView>
              ))}
            </RNView>
            {(money_.length > 0 || order.grandTotal != null) && (
              <RNView style={[S.bandMoney, isMedium && S.bandMoneyWide]}>
                {money_.map(([label, value]) => (
                  <RNView key={label} style={S.moneyRow}>
                    <Text style={S.moneyLabel}>{label}</Text>
                    <Text style={S.moneyValue}>{value}</Text>
                  </RNView>
                ))}
                {order.grandTotal != null && (
                  <RNView style={[S.moneyRow, S.moneyTotal]}>
                    <Text style={S.totalLabel}>Grand total (incl. tax)</Text>
                    <Text style={S.totalValue}>{money(order.grandTotal)}</Text>
                  </RNView>
                )}
              </RNView>
            )}
          </RNView>

          {/* ── Items ── */}
          <Card
            title={`Items (${items.length})`}
            icon="package"
            S={S}
            C={C}
            style={{ padding: 0 }}
          >
            {items.length === 0 ? (
              <Text style={[S.emptySub, { padding: 20 }]}>
                No items on this order.
              </Text>
            ) : isWide ? (
              <ItemsTable items={items} isPickup={isPickup} S={S} C={C} />
            ) : (
              <RNView style={{ padding: 12, gap: 10 }}>
                {items.map((it) => (
                  <ItemCard
                    key={it._id}
                    it={it}
                    isPickup={isPickup}
                    S={S}
                    C={C}
                  />
                ))}
              </RNView>
            )}
          </Card>
        </RNView>
      </ScrollView>

      {canEdit && (
        <CreateSalesOrderModal
          visible={editOpen}
          onClose={() => setEditOpen(false)}
          editOrder={order as unknown as EditableOrder}
          loadCustomers={loaders.loadCustomers}
          loadGrowers={loaders.loadGrowers}
          loadProducts={loaders.loadProducts}
          onSubmit={saveEdit}
        />
      )}
    </View>
  );
};

export default OrganizationOrderSummaryScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const createS = (C: Tokens) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: C.surfaceAlt },
    center: {
      justifyContent: "center",
      alignItems: "center",
      gap: 8,
      padding: 32,
    },
    hover: { backgroundColor: C.surfaceAlt },

    // Header
    header: {
      backgroundColor: C.surface,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
      paddingHorizontal: 16,
      paddingVertical: 14,
    },
    headerInner: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
    },
    titleRow: { flexDirection: "row", alignItems: "center", gap: 12 },
    titleLine: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    backBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      justifyContent: "center",
      alignItems: "center",
    },
    eyebrow: { fontSize: 12, fontWeight: "600", color: C.textMuted },
    exportRow: { flexDirection: "row", gap: 8 },
    editBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      height: 38,
      minWidth: 38,
      paddingHorizontal: 12,
      borderRadius: 10,
      backgroundColor: C.accent,
      justifyContent: "center",
    },
    editText: { fontSize: 13, fontWeight: "800", color: "#fff" },
    exportBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      height: 38,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
    },
    exportText: { fontSize: 13, fontWeight: "700", color: C.text },
    title: { fontSize: 22, fontWeight: "800", color: C.text, flexShrink: 1 },

    // Layout
    content: { padding: 16, paddingBottom: 48 },
    inner: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
      gap: 16,
    },
    split: { gap: 16 },
    splitWide: { flexDirection: "row", alignItems: "stretch" },
    half: { flex: 1 },

    // Card
    card: {
      backgroundColor: C.surface,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: C.border,
      padding: 18,
      overflow: "hidden",
    },
    cardHead: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      marginBottom: 16,
    },
    cardIcon: {
      width: 30,
      height: 30,
      borderRadius: 9,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },
    cardTitle: { fontSize: 15, fontWeight: "800", color: C.text },

    // Fields
    fieldGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      rowGap: 16,
      columnGap: 20,
    },
    field: { gap: 4, minWidth: 0 },
    fieldWide: { width: "22%", minWidth: 180, flexGrow: 1 },
    fieldLabelRow: { flexDirection: "row", alignItems: "center", gap: 5 },
    fieldLabel: {
      fontSize: 11,
      fontWeight: "700",
      color: C.textMuted,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    fieldValue: { fontSize: 14, fontWeight: "600", color: C.text },

    // Tracker
    tracker: { flexDirection: "row", alignItems: "flex-start" },
    trackStep: { alignItems: "center", gap: 8, width: 72 },
    trackDot: {
      width: 32,
      height: 32,
      borderRadius: 16,
      borderWidth: 2,
      justifyContent: "center",
      alignItems: "center",
    },
    trackLine: {
      flex: 1,
      height: 3,
      borderRadius: 2,
      marginTop: 15,
      marginHorizontal: -20, // tuck under the dots (72 / 2 - 16)
    },
    trackLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: C.textMuted,
      textAlign: "center",
    },
    haltBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginTop: 14,
      padding: 10,
      borderRadius: 10,
    },
    haltText: { fontSize: 13, fontWeight: "700" },

    // Appointments
    apptRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      padding: 10,
      borderRadius: 10,
      backgroundColor: C.surfaceAlt,
    },
    apptTime: { fontSize: 13, fontWeight: "700", color: C.text },
    apptSummary: { fontSize: 13, color: C.textMuted, flex: 1 },

    // Totals band
    band: {
      backgroundColor: C.isDark ? C.surface : C.navy,
      borderRadius: 14,
      borderWidth: C.isDark ? 1 : 0,
      borderColor: C.border,
      padding: 20,
      gap: 18,
    },
    bandRow: { flexDirection: "row" },
    bandRowWrap: { flexWrap: "wrap", rowGap: 16 },
    bandStat: { flex: 1, alignItems: "center", gap: 4 },
    bandStatHalf: { flexBasis: "50%", flexGrow: 0 },
    bandValue: { fontSize: 26, fontWeight: "800", color: "#fff" },
    bandLabel: {
      fontSize: 11,
      fontWeight: "700",
      color: "rgba(255,255,255,0.6)",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    bandMoney: {
      borderTopWidth: 1,
      borderTopColor: "rgba(255,255,255,0.12)",
      paddingTop: 14,
      gap: 8,
    },
    bandMoneyWide: { alignSelf: "flex-end", minWidth: 320 },
    moneyRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 24,
    },
    moneyLabel: { fontSize: 13, color: "rgba(255,255,255,0.7)" },
    moneyValue: { fontSize: 14, fontWeight: "700", color: "#fff" },
    moneyTotal: {
      marginTop: 4,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: "rgba(255,255,255,0.12)",
    },
    totalLabel: { fontSize: 14, fontWeight: "700", color: "#fff" },
    totalValue: { fontSize: 20, fontWeight: "800", color: "#fff" },

    // Items — table
    table: { borderTopWidth: 1, borderTopColor: C.border },
    tr: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      paddingHorizontal: 18,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: C.borderLight,
    },
    thead: { backgroundColor: C.surfaceAlt, paddingVertical: 10 },
    th: {
      fontSize: 11,
      fontWeight: "800",
      color: C.textMuted,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    td: { fontSize: 13, color: C.textMid },
    cItem: { flex: 2.2, minWidth: 0 },
    cQty: { flex: 0.9 },
    cPieces: { flex: 0.8 },
    cPrice: { flex: 0.9 },
    cLoc: { flex: 2, minWidth: 0 },
    cDoc: { flex: 1.1 },
    qtyCell: { gap: 2 },
    qty: { fontSize: 15, fontWeight: "800", color: C.text },
    uom: { fontSize: 11, fontWeight: "700", color: C.textMuted },
    itemName: { fontSize: 14, fontWeight: "700", color: C.text },
    itemSub: { fontSize: 12, color: C.textMuted, marginTop: 2 },
    thumb: {
      borderRadius: 10,
      backgroundColor: C.surfaceAlt,
      borderWidth: 1,
      borderColor: C.border,
      overflow: "hidden",
      justifyContent: "center",
      alignItems: "center",
    },
    docChip: {
      alignSelf: "flex-start",
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surfaceAlt,
    },
    docText: { fontSize: 12, fontWeight: "700", color: C.text },
    locRow: { flexDirection: "row", alignItems: "flex-start", gap: 5 },
    locLabel: { fontSize: 12, fontWeight: "700", color: C.textMuted },
    locValue: { fontSize: 12, color: C.textMid, flex: 1 },

    // Items — cards
    itemCard: {
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      padding: 12,
      gap: 12,
    },
    itemTop: { flexDirection: "row", alignItems: "center", gap: 12 },
    itemStats: {
      flexDirection: "row",
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      paddingVertical: 10,
    },
    itemStat: { flex: 1, alignItems: "center", gap: 2 },
    itemStatLabel: { fontSize: 11, fontWeight: "700", color: C.textMuted },
    itemStatValue: { fontSize: 14, fontWeight: "800", color: C.text },

    // Empty
    emptyIcon: {
      width: 60,
      height: 60,
      borderRadius: 18,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 6,
    },
    emptyTitle: { fontSize: 17, fontWeight: "800", color: C.text },
    emptySub: { fontSize: 13, color: C.textMuted, textAlign: "center" },
    emptyBtn: {
      marginTop: 8,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 20,
      backgroundColor: C.accent + "14",
    },
    emptyBtnText: { fontSize: 13, fontWeight: "700", color: C.accent },
  });
